import {createFeatureSelector, createSelector} from '@ngrx/store';
import {IQuotesState} from './quotes.reducer';
import {IDebtorDetails} from '../models/debtors';
import {IQuoteListItem} from '../models/quote-list-item.interface';
import {IQuoteStockItem} from '../models/quoteStockItems';
import {QUOTES_LOCAL_DRAFT_MAX_COUNT} from '../models/quote-local-draft.interface';
import {IQuoteComposeEntry} from '../models/quote-compose-entry.interface';
import {
  IQuoteGridRow,
  mapQuoteComposeEntriesToGridRows,
  QUOTE_GRID_ROW_KIND_BLANK,
} from '../models/quote-grid-row.interface';
import {extractQuoteItemsFromComposeEntries} from '../utils/quote-compose-entry.utils';
import {QUOTES_MOBILE_CARD_LIMIT} from '../services/quotes-firebase.service';

/**
 * Feature selector for quotes state.
 */
export const selectQuotesState = createFeatureSelector<IQuotesState>('quotes');

/**
 * Whether the quotes feature page is currently active (mounted).
 */
export const selectIsQuotesFeatureActive = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.isQuotesFeatureActive,
);

/**
 * Store id the quotes feature data is currently bound to.
 */
export const selectQuotesLoadedStoreId = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.loadedStoreId,
);

/**
 * Selector for ordered compose entries (sections and items).
 */
export const selectQuoteComposeEntries = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.composeEntries,
);

/**
 * Selector for all quote stock items in compose order.
 */
export const selectQuoteItems = createSelector(
  selectQuoteComposeEntries,
  (entries: IQuoteComposeEntry[]) => extractQuoteItemsFromComposeEntries(entries),
);

/**
 * Selector for quote items count.
 */
export const selectQuoteItemsCount = createSelector(
  selectQuoteItems,
  (items) => items.length,
);

/**
 * Selector for the selected debtor.
 */
export const selectQuoteDebtor = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.debtor,
);

/**
 * Selector for the quote comment.
 */
export const selectQuoteComment = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.comment,
);

/**
 * Selector for the total value of all quote items.
 * Calculates the sum of (sellPriIncl1 * orderQty) for all items.
 */
export const selectQuoteTotal = createSelector(
  selectQuoteItems,
  selectQuoteDebtor,
  (items: IQuoteStockItem[], debtor: IDebtorDetails | null | undefined) => {
    const subtotal = items.reduce((sum, it) => {
      const price = it?.sellPriIncl1 ?? 0;
      const qty = it?.orderQty ?? 0;
      const discount = it?.itemDiscount ? (1 - (it?.itemDiscount / 100)) : 1;
      return sum + (price * qty * discount);
    }, 0);

    const discountPct = (debtor?.discount ?? 0) / 100;
    const total = subtotal * (1 - discountPct);

    return Number.isFinite(total) ? Math.round((total + Number.EPSILON) * 100) / 100 : 0;
  },
);

/**
 * Blank grid row used for inline stock code search on desktop compose.
 */
const createQuoteBlankGridRow = (): IQuoteGridRow => ({
  quoteRowKind: QUOTE_GRID_ROW_KIND_BLANK,
  avCost: null,
  barcode: null,
  binL: null,
  category: null,
  code: null,
  created: null,
  dep: null,
  desc: null,
  discExempt: null,
  dlvColl: null,
  excludeSellingValue: null,
  genCode: null,
  gp1: null,
  lastMoved: null,
  lastPurchase: null,
  lastSold: null,
  lastStockTake: null,
  latestCost: null,
  lineColourCode: null,
  maxDisc: null,
  modified: null,
  noDecimal: null,
  onHand: null,
  onHoldCode: null,
  ordLvl: null,
  packSize: null,
  prvSellingPri: null,
  prvSellingPriDate: null,
  purchaseOrder: null,
  recommendedGP: null,
  regularSuppCode: null,
  reportItemCode: null,
  reportItemFactor: null,
  salesOrder: null,
  sellIntoNegative: null,
  sellPriExcl1: null,
  sellPriIncl1: null,
  sellUnderCost: null,
  subDep: null,
  suppCode: null,
  suppUsedLast1: null,
  suppUsedLast2: null,
  suppUsedLast3: null,
  suppUsedLast4: null,
  suppUsedLast5: null,
  suppUsedLastDate1: null,
  suppUsedLastDate2: null,
  suppUsedLastDate3: null,
  suppUsedLastDate4: null,
  suppUsedLastDate5: null,
  suppUsedLastPrice1: null,
  suppUsedLastPrice2: null,
  suppUsedLastPrice3: null,
  suppUsedLastPrice4: null,
  suppUsedLastPrice5: null,
  unitsYear: null,
  user: null,
  vatR: null,
  web: null,
  orderQty: null,
  total: null,
  discount: null,
  originalSellPriIncl1: null,
  nominalGP: null,
  itemDiscount: null,
} as IQuoteGridRow);

/**
 * Mobile compose list — sections and items in display order.
 */
export const selectQuoteComposeDisplayEntries = createSelector(
  selectQuoteComposeEntries,
  (entries: IQuoteComposeEntry[]) => entries,
);

/**
 * Selector for quote items with blank row for grid display.
 * Returns mutable copies of items to prevent AG Grid from modifying frozen state.
 */
export const selectQuoteItemsWithBlank = createSelector(
  selectQuoteComposeEntries,
  (entries: IQuoteComposeEntry[]) => {
    const rows = mapQuoteComposeEntriesToGridRows(entries);
    return [createQuoteBlankGridRow(), ...rows];
  },
);

/**
 * Selector for quote processing loading state.
 */
export const selectIsProcessingQuote = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.isProcessingQuote,
);

export const selectIsLoadingQuoteDetail = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.isLoadingQuoteDetail,
);

export const selectActiveQuoteId = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.activeQuoteId,
);

export const selectActiveQuoteDraftId = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.activeDraftId,
);

export const selectIsViewingExistingQuote = createSelector(
  selectActiveQuoteId,
  (activeQuoteId: string | null) => !!activeQuoteId,
);

/**
 * Desktop compose grid rows — blank code-search row only when creating a new quote.
 */
export const selectQuoteItemsForDesktopGrid = createSelector(
  selectQuoteComposeEntries,
  selectIsViewingExistingQuote,
  (entries: IQuoteComposeEntry[], isViewingExisting: boolean): IQuoteGridRow[] => {
    const rows = mapQuoteComposeEntriesToGridRows(entries);
    if (isViewingExisting) {
      return rows;
    }
    return [createQuoteBlankGridRow(), ...rows];
  },
);

/**
 * Selector for quote processing error.
 */
export const selectProcessQuoteError = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.processQuoteError,
);

/**
 * Selector for visible fields in the quotes grid.
 */
export const selectQuotesVisibleFields = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.visibleFields,
);

export const selectQuotesFirestoreList = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.quotesList,
);

export const selectLocalDraftQuotes = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.localDraftQuotes,
);

export const selectLocalDraftQuotesCount = createSelector(
  selectLocalDraftQuotes,
  (drafts: IQuoteListItem[]) => drafts.length,
);

export const selectIsQuoteLocalDraftLimitReached = createSelector(
  selectLocalDraftQuotesCount,
  (count: number) => count >= QUOTES_LOCAL_DRAFT_MAX_COUNT,
);

/**
 * Firestore quotes with local drafts shown first.
 */
export const selectQuotesList = createSelector(
  selectLocalDraftQuotes,
  selectQuotesFirestoreList,
  (localDrafts: IQuoteListItem[], firestoreQuotes: IQuoteListItem[]) => [
    ...localDrafts,
    ...firestoreQuotes,
  ],
);

export const selectIsQuotesListLoading = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.isQuotesListLoading,
);

export const selectQuotesListLoadingPresentation = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.quotesListLoadingPresentation,
);

export const selectQuotesListError = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.quotesListError,
);

export const selectQuoteDebtors = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.debtors,
);

export const selectIsQuoteDebtorsLoading = createSelector(
  selectQuotesState,
  (state: IQuotesState) => state.isDebtorsLoading,
);

/**
 * Last 10 quotes by processed date (fallback creation date) for mobile cards.
 */
export const selectQuotesMobileCards = createSelector(
  selectQuotesList,
  (quotes: IQuoteListItem[]) => quotes.slice(0, QUOTES_MOBILE_CARD_LIMIT),
);

